import React, { Suspense } from 'react'
import { useEffect } from 'react'
import { Outlet, matchRoutes, useLocation, useNavigation } from 'react-router-dom'

import { Dialog } from '@frame/components/dialog'
import { Loading } from '@frame/components/screens/non-terminal/loading'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useStore } from '@frame/lib/stores/useStore'
import { trackView } from '@frame/lib/tracking'
import { isSessionInTerminalState } from '@frame/lib/utils/session'
import { internalRoutes, routes } from '@frame/routes'

function computeViewName(routeMatches: { route: { path?: string } }[]) {
  let viewName = ''
  for (let index = 0; index < routeMatches.length; index++) {
    const routeMatch = routeMatches[index]
    let path = routeMatch.route.path
    // Skip pathless routes
    if (!path) {
      continue
    }

    // Remove trailing "/*" if present
    path = path.replace(/\/\*$/, '')

    if (path.startsWith('/')) {
      // Handle absolute child route paths
      viewName = path
    } else {
      // Handle route paths ending with "/"
      viewName += viewName.endsWith('/') ? path : `/${path}`
    }
  }

  return viewName || '/'
}

export function Layout() {
  // This component is used for any logic that needs to be applied *inside* the router context. If we don't need to be
  // running inside of <Router>, we can just use the App component directly.
  const location = useLocation()
  const navigation = useNavigation()
  const { redirectToSessionStateRoute } = useAutoNavigate()
  const state = useStore((state) => state.session?.state)

  useEffect(() => {
    const routeMatches = matchRoutes(
      Object.values(routes)
        .concat(Object.values(internalRoutes))
        .map((p) => ({ path: `${p}/*` })), // Match routes with id param
      location.pathname
    )
    const viewName = routeMatches && computeViewName(routeMatches)

    if (viewName) {
      trackView(viewName)
    }
  }, [location.pathname])

  useEffect(() => {
    /* If the session is in a terminal state, redirect to the appropriate route.
    This is necessary at the layout-level because a user can "go back" using the browser's back button from a 
    terminal screen. This may result in odd behavior and excpetions being thrown from the backend.
    */
    if (isSessionInTerminalState(state)) {
      redirectToSessionStateRoute()
    }
  }, [location.pathname, state, redirectToSessionStateRoute])

  // Note that this only handles the "loading" state -- the "submitting" state is not handled [yet?].
  // https://reactrouter.com/en/main/hooks/use-navigation#navigationstate
  return (
    <Dialog>
      <Suspense fallback={<Loading />}>{navigation.state === 'loading' ? <Loading /> : <Outlet />}</Suspense>
    </Dialog>
  )
}
